import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAuthFromStorage, setAuthToStorage } from '@/lib/auth-storage'
import { useAuthStore } from '@/stores/auth-store'

export type Role = 'MANAGER' | 'DOCTOR'

export interface User {
  id: string
  email: string
  name: string
  role: Role
  imageUrl: string | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  expiresAt: number | null // epoch ms
}

interface AuthContextValue {
  state: AuthState
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function scheduleTimer(expiresAt: number | null, cb: () => void) {
  if (!expiresAt) return () => {}
  const ms = expiresAt - Date.now() - 60 * 1000 // refresh 60s before expiry
  if (ms <= 0) {
    // immediate
    cb()
    return () => {}
  }
  const id = window.setTimeout(cb, ms)
  return () => window.clearTimeout(id)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { auth } = useAuthStore()
  const [state, setState] = useState<AuthState>(() => {
    const init = getAuthFromStorage()
    return (
      init ?? { user: null, accessToken: null, refreshToken: null, expiresAt: null }
    )
  })
  const [loading, setLoading] = useState(false)

  // keep zustand store in sync for compatibility
  useEffect(() => {
    auth.setUser(state.user as any)
    if (state.accessToken) auth.setAccessToken(state.accessToken)
  }, [state?.accessToken, state?.user])

  // schedule auto refresh
  useEffect(() => {
    let cancel = () => {}
    if (state.refreshToken && state.expiresAt) {
      cancel = scheduleTimer(state.expiresAt, async () => {
        try {
          await refreshToken()
        } catch {
          signOut()
        }
      })
    }
    return cancel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.refreshToken, state.expiresAt])

  async function signIn(email: string, password: string) {
    setLoading(true)
    try {
      const res = await api.post('/auth/login', { email, password })
      const data = res.data as any

      if (data.user?.role === 'PATIENT') {
        throw new Error('No tienes permiso para acceder a esta aplicación.')
      }

      const expiresAt = Date.now() + (data.expiresIn ?? 900) * 1000

      const newState: AuthState = {
        user: data.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt,
      }

      setState(newState)
      setAuthToStorage(newState)
    } finally {
      setLoading(false)
    }
  }

  async function refreshToken() {
    const stored = getAuthFromStorage()
    if (!stored?.refreshToken) throw new Error('no refresh token')
    setLoading(true)
    try {
      const res = await api.post('/auth/refresh', { refreshToken: stored.refreshToken })
      const data = res.data
      const expiresAt = Date.now() + (data.expiresIn ?? 900) * 1000
      const newState: AuthState = {
        user: data.user ?? stored.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? stored.refreshToken,
        expiresAt,
      }
      setState(newState)
      setAuthToStorage(newState)
    } finally {
      setLoading(false)
    }
  }

  function signOut() {
    setState({ user: null, accessToken: null, refreshToken: null, expiresAt: null })
    auth.reset()
  }

  const value = useMemo(
    () => ({ state, loading, signIn, signOut }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, loading]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
